import type {
  ConfirmatoryQuestion,
  QuestionResponse,
  QuestionSelection,
  RevealedWithoutAnswering,
} from "./types";

/** Doc 04 section 12: a random six of the available set each run. */
export const QUESTIONS_PER_RUN = 6;

/**
 * Small deterministic generator, so a seeded run replays exactly. Seeding is
 * what makes the selection testable; an unseeded run varies so the bank does
 * not become memorised as a fixed sequence.
 */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let drawn = Math.imul(state ^ (state >>> 15), 1 | state);
    drawn = (drawn + Math.imul(drawn ^ (drawn >>> 7), 61 | drawn)) ^ drawn;
    return ((drawn ^ (drawn >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Selects questions without replacement. Fewer than requested is reported as
 * an issue and never padded, because a padded bank would either repeat a
 * question or invent one.
 */
export function selectQuestions(
  bank: readonly ConfirmatoryQuestion[],
  random: () => number,
  requested: number = QUESTIONS_PER_RUN,
): QuestionSelection {
  const pool = [...bank];
  const chosen: ConfirmatoryQuestion[] = [];

  while (chosen.length < requested && pool.length > 0) {
    const index = Math.floor(random() * pool.length) % pool.length;
    const [picked] = pool.splice(index, 1);
    /* v8 ignore next -- splice on a non-empty pool always yields an element */
    if (picked === undefined) break;
    chosen.push(picked);
  }

  return {
    issue: chosen.length < requested ? "INSUFFICIENT_QUESTIONS" : null,
    questions: chosen,
    requested,
  };
}

/** A response counts as answered only if it carries a non-whitespace typed value. */
export function isAnswered(response: QuestionResponse): boolean {
  return response.typed.trim() !== "";
}

/** The stored signal: revealed, but never answered. */
export function revealedWithoutAnswering(
  questions: readonly ConfirmatoryQuestion[],
  responses: readonly QuestionResponse[],
): readonly RevealedWithoutAnswering[] {
  const byId = new Map(questions.map((question) => [question.id, question]));
  return responses
    .filter((response) => response.revealed && !isAnswered(response))
    .flatMap((response) => {
      const question = byId.get(response.questionId);
      return question === undefined
        ? []
        : [{ questionId: question.id, nodeId: question.nodeId }];
    });
}
